import { useEffect, useRef, useState } from 'react';
import { MapPin, Camera, CheckCircle2, Loader2, AlertTriangle, LogIn, LogOut, RotateCcw } from 'lucide-react';
import { Modal, Button, message } from '../../ui';
import { api, request } from '../../api/client';
import { getDeviceFix, type DeviceFix } from '../../hooks/useDeviceFix';

/**
 * PUNCH IN / PUNCH OUT — the same steps as the Android punch sheet: a live location fix, a selfie, then
 * the punch. Each punch of a shift keeps ITS OWN selfie (check_in_selfie_url / check_out_selfie_url on the
 * shift), which the attendance record shows shift by shift.
 *
 * The selfie is asked for, not forced — exactly as on Android: a camera that fails or is refused must
 * never keep a rep from marking attendance. The location is NOT optional: no fix, no punch (the server
 * enforces the accuracy floor and the mock block too).
 */
type Step = 'locating' | 'located' | 'failed';

export function PunchModal({ mode, open, onClose, onDone }: {
  mode: 'in' | 'out'; open: boolean; onClose: () => void; onDone: () => void;
}) {
  const isIn = mode === 'in';
  const [step, setStep] = useState<Step>('locating');
  const [fix, setFix] = useState<DeviceFix | null>(null);
  const [fixError, setFixError] = useState<string | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const locate = () => {
    setStep('locating'); setFixError(null);
    getDeviceFix()
      .then((f) => { setFix(f); setStep('located'); })
      .catch((e: any) => { setFix(null); setStep('failed'); setFixError(e?.message ?? 'Could not get your location.'); });
  };
  // A fresh sheet every time it opens: a new fix, no selfie left over from the last punch.
  useEffect(() => {
    if (!open) return;
    setSelfie(null); setPreview(null); setBusy(false);
    locate();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pick = (f: File | undefined) => {
    if (!f) return;
    setSelfie(f);
    setPreview(URL.createObjectURL(f));
    if (input.current) input.current.value = '';
  };

  const confirm = async () => {
    if (!fix) return;
    setBusy(true);
    try {
      let selfieUrl: string | undefined;
      if (selfie) {
        try {
          const form = new FormData();
          form.append('file', selfie, `punch-${mode}-selfie.jpg`);
          const up = await api.post('/uploads/selfie', form, { headers: { 'Content-Type': 'multipart/form-data' } });
          selfieUrl = (up.data?.data ?? up.data)?.url ?? undefined;
        } catch {
          // Best-effort, as on Android — but never silently: the rep hears that the photo didn't go.
          message.warning('The selfie could not be uploaded — punching without it.');
        }
      }
      await request({
        url: isIn ? '/attendance/check-in' : '/attendance/check-out', method: 'POST',
        data: {
          lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM, isMockLocation: fix.isMockLocation, selfieUrl,
          ...(isIn ? { addressText: `lat ${fix.lat.toFixed(4)}, lng ${fix.lng.toFixed(4)}` } : {}),
        },
      });
      message.success(isIn ? 'Punched in — attendance marked PRESENT' : 'Punched out — day completed');
      onDone();
      onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? e?.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const row = (icon: JSX.Element, title: string, sub: string, state: 'done' | 'current' | 'error' | 'todo', action?: JSX.Element) => (
    <div className={`flex items-center gap-3 rounded-card border px-3 py-2.5 ${
      state === 'error' ? 'border-danger-500/40 bg-danger-500/5' : state === 'done' ? 'border-brand-500/30 bg-brand-500/5' : 'border-line bg-surface'}`}>
      <span className={`grid size-9 shrink-0 place-items-center rounded-full ${
        state === 'error' ? 'bg-danger-500/10 text-danger-600' : state === 'done' ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300' : 'bg-surface-2 text-subtle'}`}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="type-body-strong text-content">{title}</div>
        <div className={`type-caption ${state === 'error' ? 'text-danger-600 dark:text-danger-500' : 'text-subtle'}`}>{sub}</div>
      </div>
      {action}
    </div>
  );

  return (
    <Modal
      open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }} width={460}
      title={isIn ? 'Punch in' : 'Punch out'}
      description={isIn ? 'Geo-stamped attendance: your location now, and a selfie.' : 'Ends your current shift. You can punch in again later if you head back out.'}
      footer={
        <>
          <Button variant="default" onClick={onClose} disabled={busy}>{isIn ? 'Cancel' : 'Stay on duty'}</Button>
          <Button variant={isIn ? 'primary' : 'danger'} icon={isIn ? <LogIn size={16} /> : <LogOut size={16} />}
            loading={busy} disabled={step !== 'located'} onClick={confirm}>
            {isIn ? 'Confirm punch in' : 'Confirm punch out'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2.5">
        {row(
          step === 'locating' ? <Loader2 size={17} className="animate-spin" /> : step === 'failed' ? <AlertTriangle size={17} /> : <MapPin size={17} />,
          step === 'locating' ? 'Getting your location…' : step === 'failed' ? 'No location' : 'Location captured',
          step === 'failed' ? (fixError ?? 'Turn on location and try again.')
            : step === 'located' && fix ? `${fix.lat.toFixed(5)}, ${fix.lng.toFixed(5)}${fix.accuracyM != null ? ` · ±${Math.round(fix.accuracyM)} m` : ''}`
            : 'A live GPS fix — required to punch.',
          step === 'failed' ? 'error' : step === 'located' ? 'done' : 'current',
          step === 'failed' ? <Button size="sm" variant="default" icon={<RotateCcw size={14} />} onClick={locate}>Retry</Button> : undefined,
        )}
        {row(
          preview ? <img src={preview} alt="Your selfie" className="size-9 rounded-full object-cover" /> : <Camera size={17} />,
          preview ? 'Selfie taken' : `Selfie at ${isIn ? 'punch in' : 'punch out'}`,
          preview ? 'Kept on this shift in your attendance record.' : 'Take a live selfie — recommended, not required.',
          preview ? 'done' : 'current',
          <Button size="sm" variant={preview ? 'default' : 'primary'} icon={<Camera size={14} />} onClick={() => input.current?.click()} disabled={busy}>
            {preview ? 'Retake' : 'Take selfie'}
          </Button>,
        )}
        {step === 'located' && (
          <div className="flex items-center gap-1.5 type-caption text-subtle">
            <CheckCircle2 size={13} className="text-brand-600" />
            Ready — {preview ? 'your selfie and location go with this punch.' : 'your location goes with this punch.'}
          </div>
        )}
        {/* capture="user" opens the front camera on a phone; a desktop falls back to choosing a picture. */}
        <input ref={input} type="file" accept="image/*" capture="user" className="hidden" aria-label="Selfie"
          onChange={(e) => pick(e.target.files?.[0])} />
      </div>
    </Modal>
  );
}
